import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import { formatPriceInterval } from "@/v2-backoffice-client/license/plan/license.plan.seatModel.utils"
import type { CustomerPricingQuery } from "@/v2-backoffice-client/customer/pricing/__types__/customer.pricing.query"

export type CustomerPricingLine =
  CustomerPricingQuery["getCustomerPricing"][number]

type ContractTier = CustomerPricingLine["Tiers"][number]

// Below half a unit the billed and list prices are the same price: Fenerum
// rounds per period.
const SAME_PRICE_TOLERANCE = 0.5

export const PRICING_DEAL_KIND = {
  /** Bills at the term's list price. */
  List: "list",
  /** A price or discount set on the subscription itself in Fenerum. */
  Subscription: "subscription",
  /** A contract on the account for this plan term, with quantity bands. */
  Contract: "contract",
  /** Billed off list with nothing mirrored that explains it. */
  Unexplained: "unexplained",
} as const

export type PricingDealKind =
  (typeof PRICING_DEAL_KIND)[keyof typeof PRICING_DEAL_KIND]

export type PricingDeal = {
  kind: PricingDealKind
  /** The one-line answer in the Deal cell. */
  summary: string
  /** The rest, one sentence per fact (binding period, every band, when contracts were read). */
  details: string[]
}

function hasOwnDeal(line: CustomerPricingLine) {
  return (
    line.OverridePrice != null ||
    line.DiscountPercentage != null ||
    line.DiscountFixedPerQuantity != null ||
    line.DiscountFixedTotal != null
  )
}

/** Whether the billed price differs from the list price for the quantity. */
export function isOffList(line: CustomerPricingLine) {
  if (line.BilledPrice == null || line.ListPrice == null) return false
  return Math.abs(line.BilledPrice - line.ListPrice) > SAME_PRICE_TOLERANCE
}

/** The gap as a signed percentage of list ("-15" for 15 % below list); null when either side is missing or list is zero. */
export function discountPercentOf(line: CustomerPricingLine) {
  if (line.BilledPrice == null || !line.ListPrice) return null
  return ((line.BilledPrice - line.ListPrice) / line.ListPrice) * 100
}

function unitWord(line: CustomerPricingLine) {
  // Seat lines and quantity plans sell seats (lic.SeatSources code 1);
  // anything else is a unit of the subscription.
  return line.SeatSourceCode === 1 ? "seat" : "unit"
}

function formatAmount(amount: number, currency: string | null | undefined) {
  return currency ? `${formatNumber(amount)} ${currency}` : formatNumber(amount)
}

/** One band as a sentence: "11+ seats: 15 % off the seats above 10". */
export function describeTier(tier: ContractTier, line: CustomerPricingLine) {
  const unit = unitWord(line)
  const range =
    tier.MaximumQuantity > 0
      ? `${tier.MinimumQuantity}–${tier.MaximumQuantity} ${unit}s`
      : `${tier.MinimumQuantity}+ ${unit}s`
  const discount =
    tier.Discount == null
      ? null
      : tier.DiscountTypeCode === "percent"
        ? `${formatNumber(tier.Discount, 2)} % off`
        : `${formatAmount(tier.Discount, line.Currency)} off per ${unit}`
  const scope = tier.IsOnlyAboveMinimum
    ? tier.MinimumQuantity > 1
      ? ` the ${unit}s above ${tier.MinimumQuantity - 1}`
      : ""
    : discount
      ? ` every ${unit}`
      : ""
  const flat =
    tier.OverallFlatDiscount && tier.OverallFlatDiscount > 0
      ? `${formatAmount(tier.OverallFlatDiscount, line.Currency)} off the line`
      : null
  const parts = [discount ? `${discount}${scope}` : null, flat].filter(
    (part): part is string => !!part,
  )
  return `${range}: ${parts.length > 0 ? parts.join(", plus ") : "no discount"}`
}

/**
 * The one rule for the Deal cell: what explains the gap between list and
 * billed on a line, in the words a CSM uses with the customer. Read in this
 * order — the deal on the subscription itself, then the account's contract,
 * then "nothing on record" when the price is off list anyway.
 */
export function describePricingDeal(line: CustomerPricingLine): PricingDeal {
  const details: string[] = []
  const interval = formatPriceInterval(line.IntervalType, line.IntervalCount)
  const unit = unitWord(line)

  let summary: string | null = null
  let kind: PricingDealKind = PRICING_DEAL_KIND.List

  if (line.OverridePrice != null) {
    kind = PRICING_DEAL_KIND.Subscription
    summary = `Agreed price ${formatAmount(line.OverridePrice, line.Currency)} per ${unit}${interval}`
  } else if (line.DiscountPercentage != null) {
    kind = PRICING_DEAL_KIND.Subscription
    summary = `${formatNumber(line.DiscountPercentage, 2)} % off the subscription`
  } else if (line.DiscountFixedPerQuantity != null) {
    kind = PRICING_DEAL_KIND.Subscription
    summary = `${formatAmount(line.DiscountFixedPerQuantity, line.Currency)} off per ${unit}`
  } else if (line.DiscountFixedTotal != null) {
    kind = PRICING_DEAL_KIND.Subscription
    summary = `${formatAmount(line.DiscountFixedTotal, line.Currency)} off the line`
  }

  if (line.ContractId != null) {
    const tiers = [...line.Tiers].sort(
      (a, b) => a.MinimumQuantity - b.MinimumQuantity,
    )
    const bands = tiers.map((tier) => describeTier(tier, line))
    const contractSummary =
      bands.length === 0
        ? "Contract, no discount bands"
        : bands.length === 1
          ? `Contract: ${bands[0]}`
          : `Contract with ${bands.length} price bands`
    if (summary) {
      // Fenerum does not combine a subscription discount with a contract
      // on the same account and plan term; both showing means the mirror
      // caught a change mid-way. Say both rather than hide one.
      details.push(contractSummary)
    } else {
      kind = PRICING_DEAL_KIND.Contract
      summary = contractSummary
    }
    if (bands.length > 1) details.push(...bands)
    if (bands.length > 1 && line.IsExclusiveTiers != null) {
      details.push(
        line.IsExclusiveTiers
          ? "Only the band giving the lowest price applies."
          : "Every band whose range is met applies.",
      )
    }
    if (line.CommitmentEndsAt) {
      details.push(`Bound until ${formatDateString(line.CommitmentEndsAt)}.`)
    }
    if (line.ContractEndDate) {
      details.push(`Contract ends ${formatDateString(line.ContractEndDate)}.`)
    }
  }

  if (!summary) {
    if (isOffList(line)) {
      kind = PRICING_DEAL_KIND.Unexplained
      summary = "Off list, nothing on record"
      details.push(
        line.ContractsSyncedAt
          ? `The account's contracts were read ${formatDateString(line.ContractsSyncedAt)} and none covers this license; the price may be set on the subscription in Fenerum in a way the mirror does not carry.`
          : "The account's contracts have not been read yet; the next sync (within the hour) picks them up.",
      )
    } else {
      summary = "List price"
    }
  }

  return { kind, summary, details }
}

/** Months in a billing interval, for normalising a period price to a month. */
function monthsOf(line: CustomerPricingLine) {
  const count = line.IntervalCount ?? 1
  switch (line.IntervalType) {
    case "year":
      return 12 * count
    case "month":
      return count
    case "day":
      return count / 30
    default:
      return null
  }
}

export type CustomerPricingSummary = {
  listMonthly: number
  billedMonthly: number
  currency: string | null
  /** Lines whose interval could not be normalised and are left out of the monthly figures. */
  skipped: number
}

/**
 * The section's headline: what the customer pays a month across every live
 * subscription (Fenerum's own MRR) against the same lines at list price.
 * Null when nothing can be summed (no lines, mixed currencies, or no list
 * price on any line).
 */
export function summarizeCustomerPricing(
  lines: readonly CustomerPricingLine[],
): CustomerPricingSummary | null {
  const currencies = new Set(
    lines.map((line) => line.Currency).filter((currency) => !!currency),
  )
  if (lines.length === 0 || currencies.size > 1) return null

  let listMonthly = 0
  let billedMonthly = 0
  let counted = 0
  let skipped = 0
  for (const line of lines) {
    const months = monthsOf(line)
    if (line.ListPrice == null || months == null || months <= 0) {
      skipped++
      continue
    }
    listMonthly += line.ListPrice / months
    billedMonthly +=
      line.CurrentMrr ??
      (line.BilledPrice != null ? line.BilledPrice / months : 0)
    counted++
  }
  if (counted === 0) return null

  return {
    listMonthly,
    billedMonthly,
    currency: [...currencies][0] ?? null,
    skipped,
  }
}
